import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, Mail, MessageSquare } from 'lucide-react';

import { AppRoutes } from '@/utils/routes/router';

interface ChannelCardProps {
  onClick: () => void;
  icon: ReactNode;
  label: string;
  description: string;
}

const ChannelCard = ({ onClick, icon, label, description }: ChannelCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className="flex flex-col items-start text-left rounded-xl border border-custom-lightSilver bg-white p-6 shadow-[2px_2px_4px_rgba(0,0,0,0.04)] transition-colors hover:border-custom-blue/40 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-custom-blue/30"
  >
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-custom-silver/50 text-custom-blue">
      {icon}
    </div>
    <span className="text-base font-semibold text-custom-black">{label}</span>
    <span className="mt-1 text-sm text-custom-darkSilver">{description}</span>
  </button>
);

const SingleSendsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full">
      <div className="mb-8">
        <p className="text-sm font-medium text-custom-blue">{t('SingleSendsPage.title')}</p>
        <h1 className="mt-1 text-xl font-semibold text-custom-black">{t('SingleSendsPage.subtitle')}</h1>
      </div>
      <div className="grid w-full max-w-4xl grid-cols-1 gap-6 sm:grid-cols-3">
        <ChannelCard
          onClick={() => navigate(AppRoutes.newSMS)}
          icon={<MessageSquare className="h-6 w-6" strokeWidth={1.75} />}
          label={t('SingleSendsPage.smsLabel')}
          description={t('SingleSendsPage.smsDescription')}
        />
        <ChannelCard
          onClick={() => navigate(AppRoutes.newEmail)}
          icon={<Mail className="h-6 w-6" strokeWidth={1.75} />}
          label={t('SingleSendsPage.emailLabel')}
          description={t('SingleSendsPage.emailDescription')}
        />
        <ChannelCard
          onClick={() => navigate(AppRoutes.newPush)}
          icon={<Bell className="h-6 w-6" strokeWidth={1.75} />}
          label={t('SingleSendsPage.pushNotificationsLabel')}
          description={t('SingleSendsPage.pushDescription')}
        />
      </div>
    </div>
  );
};

export default SingleSendsPage;
